import { Form, FormRenderProps, FormSubmitClickEvent } from "@progress/kendo-react-form";
import { Error, Label } from "@progress/kendo-react-labels";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { submitQuote } from "../../api/quotes";
import quotesApi from "../../api/quotes";
import { DealEntryFormRender } from "../../components/quotes/dealentry";
import { UserCompany } from "../../hooks/useCompanies";
import { useUserContext } from "../../hooks/useUserContext";
import { StepsInterface } from "./workflow";
import { LoadingIndicator } from "../../components/LoadingIndicator";
import { useApi } from "../../hooks/useApi";
import { getQuoteDetails } from "../../api/vanilla";
import DateUtility from "../../utilities/dateUtilities";

export const DealEntryWorkflow = () => {
  const { context: user } = useUserContext();
  const { roles, permissions } = user;
  const navigate = useNavigate();
  const { state } = useLocation();
  const { data } = state || {};

  const hasSubmitPermission =
    permissions.canSubmitQuotes && roles.canSubmitQuotes(data?.company?.role);
  const hasCredit = data?.company?.features?.hasCredit;
  const canSubmit = hasSubmitPermission && hasCredit;

  const isRiskTaker = user.roles.isRiskTaker;

  const autoApprove = roles.isRiskTaker && canSubmit && permissions.canApproveTrades;
  const submitLabel = autoApprove ? "Confirm" : canSubmit ? "Submit" : "Finish";

  const [step, setStep] = useState(0);
  const [steps, setSteps] = useState<Array<StepsInterface>>([
    { label: "Product", isValid: undefined },
    { label: "Contract", isValid: undefined },
    { label: "Structure", isValid: undefined },
    { label: "Quote", isValid: undefined },
    { label: "Summary", isValid: undefined },
  ]);

  const [formState, setFormState] = useState<any | null>(null);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const lastStepIndex = steps.length - 1;
  const isQuoteStep = step === lastStepIndex - 2;
  const isSubmitStep = step === lastStepIndex;
  const isPreviousStepsValid =
    steps.slice(0, step).findIndex((currentStep) => currentStep.isValid === false) === -1;

  const getQuoteDetailsApi = useApi(getQuoteDetails);

  useEffect(() => {
    if (!state) navigate("/");
    else if (data?.quoteId) {
      getQuoteDetailsApi.request(data.quoteId);
      return;
    }

    let initialState = {
      strikeType: "Fixed",
      direction: 1,
      quoteId: 0,
      commonStrike: true,
      originalAllocationId: null,
    };

    if (data) {
      const productState = getProductState();
      if (productState) {
        setStep(1);
        Object.assign(initialState, productState);
      }
    } else setStep(0);

    setFormState(initialState);
  }, []);

  const getProductState = () => {
    if (data) {
      const { company, commodity, instrument } = data;

      if (company && commodity && instrument)
        return {
          company: { label: company.name, value: company.id },
          commodity: { label: commodity.name, value: commodity.id },
          instrument: { label: instrument.name, value: instrument.id },
          product: { commodity, instrument, company: UserCompany.create(company) },
          quoteName: `${commodity.name} (${commodity.abbreviation}) ${instrument.name}`,
        };
    }
    return null;
  };

  useEffect(() => {
    if (!isSubmitStep) {
      setStatus("");
      setError("");
    }
  }, [step]);

  useEffect(() => {
    if (!getQuoteDetailsApi.data) return;

    const {
      quote: {
        clientLong,
        configurationId,
        endDate,
        prices,
        quoteName,
        settlementTypeId,
        startDate,
        structureId,
        totalVolume,
        hasCommonStrike,
        hasFixedStrike,
        allocationId,
      },
    } = getQuoteDetailsApi.data;

    const direction = clientLong ? 1 : 2;
    const { instrument } = data;

    const contractStart = DateUtility.convertToDateWithOffset(
      startDate,
      -1 * instrument.calendarMonthOffset,
    );
    const contractEnd = DateUtility.convertToDateWithOffset(
      endDate,
      -1 * instrument.calendarMonthOffset,
    );

    const productState = getProductState();
    productState.quoteName = quoteName;

    const newFormState = {
      quoteId: data.quoteId,
      direction,
      settlement: settlementTypeId,
      configurationId,
      contract: {
        start: contractStart,
        end: contractEnd,
      },
      totalVolume,
      structureId,
      prices,
      commonStrike: hasCommonStrike,
      strikeType: hasFixedStrike ? "Fixed" : "Adder",
      originalAllocationId: allocationId,
    };

    setFormState(Object.assign({}, newFormState, productState));
    setStep(2);
  }, [getQuoteDetailsApi.data]);

  const queryClient = useQueryClient();
  const submitMutation = useMutation({
    mutationFn: (quote: any) => {
      setLoading(true);
      setStatus("Submitting quote...");
      return submitQuote(quote.pricingId, quote.quoteId, isRiskTaker);
    },
    onSuccess: () => {
      if (!autoApprove) navigate("/?tab=derivatives");
      else {
        queryClient.invalidateQueries({ queryKey: ["quotes"] });
        queryClient.invalidateQueries({ queryKey: ["quote", formState.quoteId] });
        approveMutation.mutate();
      }
    },
    onError: (error: string) => {
      setError(error);
      setLoading(false);
    },
  });

  const approveMutation = useMutation({
    mutationFn: () => {
      setLoading(true);
      setStatus("Approving quote...");
      return quotesApi.reviewQuote({
        quoteId: formState.quoteId,
        pricingId: formState.pricingId,
        isApproval: true,
        totalVolume: formState.totalVolume,
        priceOverride: null,
        brokerPriceOverride: null,
        comments: "Deal Entry",
      });
    },
    onSuccess: () => {
      navigate("/?tab=derivatives");
    },
    onError: (error: string) => {
      setError(error);
      setLoading(false);
    },
  });

  const onCancelClick = (event: any) => {
    event.preventDefault();
    navigate("/");
  };

  const onEditClick = useCallback(
    (newStep: number) => {
      setStep(Math.max(newStep, 0));
    },
    [step, setStep],
  );

  const onStepSubmit = useCallback(
    async (event: FormSubmitClickEvent) => {
      let { isValid, values } = event;

      if (!values.quoteName && values.product) {
        const { commodity, instrument } = values.product;
        values.quoteName = `${commodity.name} (${commodity.abbreviation}) ${instrument.name}`;
      }

      if (isSubmitStep && isValid) {
        if (canSubmit)
          submitMutation.mutate({ pricingId: values.pricingId, quoteId: values.quoteId });
        else {
          navigate("/");
          return;
        }
      }

      const currentSteps = steps.map((currentStep: StepsInterface, index: number) => ({
        ...currentStep,
        isValid: index === step ? isValid : currentStep.isValid,
      }));

      setSteps(currentSteps);

      if (!isValid) return;

      setStep(Math.min(step + 1, lastStepIndex));
      setFormState(values);
    },
    [steps, isQuoteStep, isSubmitStep, step, lastStepIndex],
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <div style={{ alignSelf: "center", margin: "1rem 0" }} className="k-card-title">
        {`${data?.quoteId > 0 ? "Edit Quote" : "New Quote"}`}
      </div>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <div style={{ flexBasis: "80%" }}>
          {formState && (
            <Form
              initialValues={formState}
              onSubmitClick={onStepSubmit}
              render={(formRenderProps: FormRenderProps) => (
                <DealEntryFormRender
                  step={step}
                  formRenderProps={formRenderProps}
                  isLoading={loading}
                  isSubmitStep={isSubmitStep}
                  isPreviousStepsValid={isPreviousStepsValid}
                  submitLabel={submitLabel}
                  product={formState?.product}
                  onCancelClick={onCancelClick}
                  onEditClick={onEditClick}
                />
              )}
            />
          )}

          <div style={{ float: "right" }}>
            {error && <Error>{error}</Error>}
            <LoadingIndicator loading={status.length > 0} size={"small"} />
            <Label>{status}</Label>
          </div>
        </div>
      </div>
    </div>
  );
};
